<template>
    <div class="pa-4">
        <v-row>
            <v-col cols="12" sm="6">
                <v-select :label="$t('field.entity')" v-model="entitySelector.selectedItem" :ref="el => entitySelector.component = el"
                @update:model-value="onEntitySelectorChange" variant="outlined"
                :items="entities" item-title="name" item-value="value" />
            </v-col>

            <v-col cols="12" sm="6">
                <v-select :label="$t('field.applicationDomain')" v-model="appDomainSelector.selectedItem" :ref="el => appDomainSelector.component = el"
                @update:model-value="onAppDomainSelectorChange" variant="outlined" :items="attributeApplicationDomains"
                item-title="name" item-value="id" :disabled="entitySelector.selectedItem == null || entitySelector.selectedItem == '' || entitySelector.selectedItem == 'DeliveryLocation'" />
            </v-col>
        </v-row>

        <v-divider class="mb-4" />

        <AgGrid v-if="entitySelector.selectedItem || appDomainSelector.selectedItem" ref="attributeGridRef" :fetchUrl="attributeFetchUrl"
        :columnDefs="attributeColumnDefs" :gridParams="attributeGridParams" :stepLoad="attributeStepLoad"
        :refresh="attributeRefresh" :domLayout="domLayout" />
    </div>

    <!-- Add Attribute Dialog -->
    <v-dialog v-model="addAttributeDialogVisible" class="w-33">
        <v-card>
            <v-card-title>{{ $t('dialog.newAttribute') }}</v-card-title>

            <v-divider />

            <v-form @submit.prevent v-model="vformAttribute">
                <v-card-text>
                    <v-container>
                        <v-text-field v-model="newAttribute.name" :label="$t('field.label')" variant="outlined" required maxlength="50" counter="50" :rules="[rules.required, rules.uniqueAttributeName]" />

                        <v-select v-model="newAttribute.type" :label="$t('field.type')" variant="outlined" required :rules="[rules.required]" :items="translatedAttributeTypes" item-title="label" item-value="id" />

                        <v-select v-if="showContextType" v-model="newAttribute.contextType" :label="$t('field.contextType')" variant="outlined" required :rules="[rules.required]" :items="translatedContextTypes" item-title="label" item-value="id" />

                        <v-checkbox v-model="newAttribute.requiredForPublishing" :label="$t('field.requiredForPublishing')" density="compact" hide-details />
                        <v-checkbox v-model="newAttribute.ediSet" :label="$t('field.ediOnly')" density="compact" hide-details />
                    </v-container>
                </v-card-text>

                <v-divider />

                <v-card-actions class="justify-end">
                    <v-btn color="error" variant="text" @click="addAttributeDialogVisible = false">{{ $t('action.cancel') }}</v-btn>
                    <v-btn color="primary" variant="flat" @click="saveAttribute" :disabled="!vformAttribute">{{ $t('action.ok') }}</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>

    <!-- Delete Attribute Dialog -->
    <v-dialog v-model="deleteAttributeDialogVisible" class="w-33">
        <v-card>
            <v-card-title>{{ $t('dialog.deleteAttribute') }}</v-card-title>
            <v-divider />
            <v-card-text>{{ $t('dialog.sureToDeleteAttribute') }}</v-card-text>
            <v-divider />
            <v-card-actions class="justify-end">
                <v-btn color="error" variant="text" @click="deleteAttributeDialogVisible = false">{{ $t('action.cancel') }}</v-btn>
                <v-btn color="primary" variant="flat" @click="confirmDeleteAttribute">{{ $t('action.ok') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import { toast } from 'vue3-toastify';
    import entityJson from '@/components/json/entity.json';
    import attributeContextTypeJson from '@/components/json/attributeContextType.json';
    import attributeTypeJson from '@/components/json/attributeType.json';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';

    const logStore = useLogStore();
    const userStore = useUserStore();

    const props = defineProps({
        customerReference: {
            type: String,
            default: null,
        },
    });
    const emit = defineEmits(['can-add-changed']);

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');
    const getFormBody = inject('getFormBody');

    const domLayout = 'autoHeight';
    const entities = entityJson;
    const entitySelector = reactive({
        selectedItem: null,
        component: null
    });

    const attributeApplicationDomains = ref([]);
    const appDomainSelector = reactive({
        selectedItem: null,
        component: null,
    });

    const attributeContextTypes = attributeContextTypeJson;
    const attributeTypes = attributeTypeJson;
    const attributes = ref([]);

    const attributeFetchUrl = 'GetAttributes';
    const attributeColumnDefs = [
        { field: 'id', hide: true },
        {
            field: 'contextType',
            rowGroup: true,
            hide: true,
            valueGetter: (params) => {
                if (typeof params.data.contextType != 'undefined' && params.data.contextType != null && parseInt(appDomainSelector.selectedItem) == 1)
                    return translate(attributeContextTypes.filter(t => t.id == params.data.contextType).first().value.toLowerCase(), 'contextType');

                return null;
            },
        },
        {
            field: 'name',
            headerName: translate('name', 'column'),
            sort: 'asc',
        },
        {
            field: 'type',
            headerName: translate('type', 'column'),
            cellRenderer: 'attributeTypeCellRenderer',
            filterParams: {
                cellRenderer: 'attributeTypeCellRenderer',
            },
            sort: 'asc',
        },
        {
            field: 'requiredForPublishing',
            headerName: translate('requiredForPublishing', 'column'),
            cellRenderer: 'attributeBooleanCellRenderer',
            filterParams: {
                cellRenderer: 'attributeBooleanCellRenderer',
            },
        },
        {
            field: 'ediSet',
            headerName: translate('ediOnly', 'column'),
            cellRenderer: 'attributeBooleanCellRenderer',
            filterParams: {
                cellRenderer: 'attributeBooleanCellRenderer',
            },
        },
        {
            field: 'delete',
            headerName: '',
            type: 'deleteAction',
        },
    ];
    const attributeGridParams = reactive({
        token: userStore.getUser().token,
        customerReference: props.customerReference,
        entity: null,
        applicationDomainId: null,
    });

    const attributeStepLoad = ref(false);
    const attributeRefresh = ref(false);

    const addAttributeDialogVisible = ref(false);
    const vformAttribute = ref(false);
    const newAttribute = ref({
        name: '',
        type: '',
        contextType: '',
        requiredForPublishing: false,
        ediSet: false,
    });

    const deleteAttributeDialogVisible = ref(false);
    const attributeToDeleteId = ref(null);

    const rules = {
        required: v => (v !== null && v !== '' && typeof v !== 'undefined') || 'required',
        uniqueAttributeName: v => attributes.value.map(a => a.name.toLowerCase()).indexOf((v || '').toLowerCase()) == -1 || translate('attributeAlreadyTaken', 'toast'),
    };

    const showContextType = computed(() => entitySelector.selectedItem == 'Product' && appDomainSelector.selectedItem == '1');

    const canAdd = computed(() => (entitySelector.selectedItem != null && entitySelector.selectedItem != '' && appDomainSelector.selectedItem != null && appDomainSelector.selectedItem != '') || entitySelector.selectedItem == 'DeliveryLocation');

    const translatedAttributeTypes = computed(() => attributeTypes.map(t => ({ ...t, label: translate(t.value.toLowerCase(), 'varType') })));

    const translatedContextTypes = computed(() => attributeContextTypes.map(t => ({ ...t, label: translate(t.value.toLowerCase(), 'contextType') })));

    watch(canAdd, (value) => {
        emit('can-add-changed', value);
    });

    onMounted(() => {
        if(entitySelector.selectedItem == null){
            const [firstItem] = entities;
            entitySelector.component.$emit('update:modelValue', firstItem.value);
        }
    });

    function onEntitySelectorChange() {
        appDomainSelector.selectedItem = '';
        attributeApplicationDomains.value = [];

        if (entitySelector.selectedItem != 'DeliveryLocation')
            getAttributeApplicationDomains();
        else
            triggerAttributeRefresh();
    }

    function onAppDomainSelectorChange() {
        triggerAttributeRefresh();
    }

    function triggerAttributeRefresh() {
        attributeGridParams.entity = entitySelector.selectedItem;
        attributeGridParams.applicationDomainId = appDomainSelector.selectedItem;
        attributeRefresh.value = true;

        setTimeout(() => {
            attributeRefresh.value = false;
        }, 1000);
    }

    function openAddDialog() {
        newAttribute.value = {
            name: '',
            type: '',
            contextType: '',
            requiredForPublishing: false,
            ediSet: false,
        };

        addAttributeDialogVisible.value = true;
    }

    function getAttributeApplicationDomains() {
        const data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            entity: entitySelector.selectedItem,
        };

        var formBody = getFormBody(data);

        fetch(urlWebAPI + 'GetAttributeApplicationDomains', {
            method: 'POST',
            body: formBody,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            }
        })
        .then(result => result.json())
        .then(attributeApplicationDomainsResult => {
            attributeApplicationDomains.value = attributeApplicationDomainsResult;
            var [firstItem] = attributeApplicationDomains.value;
            appDomainSelector.component.$emit('update:modelValue', firstItem.id);
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing GetAttributeApplicationDomains data.');
        });
    }

    function onCellClicked(params) {
        if (typeof params.data != 'undefined' && typeof params.colDef.field != 'undefined' && params.colDef.field.indexOf('delete') > -1) {
            let forbbidenAttributes = ['format', 'specificites'.replace(/[̀-ͯ]/g, ''), 'contenance'];
            let normalizedName = params.data.name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

            if (forbbidenAttributes.indexOf(normalizedName) == -1) {
                attributeToDeleteId.value = params.data.id;
                deleteAttributeDialogVisible.value = true;
            }
        }
    }

    function saveAttribute() {
        const entityTypeEnum = {
            None: 0,
            Product: 1,
            Account: 2,
            Period: 3,
            User: 4,
            Warehouse: 5,
            Category: 6,
            ProductTree: 7,
            Campaign: 8,
            OrderLine: 9,
            Mission: 10,
            DeliveryLocation: 11,
            ShelfStatement: 12,
        };

        var attribute = {
            applicationDomainId: appDomainSelector.selectedItem != '' && appDomainSelector.selectedItem != null ? parseInt(appDomainSelector.selectedItem) : 0,
            entityType: Object.keys(entityTypeEnum).indexOf(entitySelector.selectedItem),

            name: newAttribute.value.name,
            type: parseInt(newAttribute.value.type),
            contextType: newAttribute.value.contextType != '' && newAttribute.value.contextType != null ? parseInt(newAttribute.value.contextType) : 0,

            ediSet: newAttribute.value.ediSet,
            requiredForPublishing: newAttribute.value.requiredForPublishing,
        };

        const data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            attribute: attribute,
        };

        $.postData(urlWebAPI + 'SaveAttribute', data)
            .then(() => {
                addAttributeDialogVisible.value = false;
                triggerAttributeRefresh();
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveAttribute data.');
            });
    }

    function confirmDeleteAttribute() {
        deleteAttribute(attributeToDeleteId.value);
        deleteAttributeDialogVisible.value = false;
    }

    function deleteAttribute(attributeId) {
        const data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            attribute: attributes.value.filter(a => a.id == attributeId).first(),
        };

        $.postData(urlWebAPI + 'DeleteAttribute', data)
            .then(() => {
                triggerAttributeRefresh();
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing DeleteAttribute data.');
            });
    }

    // ViewManageCustomer.vue calls openAddDialog() through a template ref. AgGrid.vue reaches
    // into onCellClicked (getParentContext) and attributes (processData, for fetchUrl
    // GetAttributes) through the $parent chain — both only see what defineExpose() exposes.
    defineExpose({ openAddDialog, onCellClicked, attributes });
</script>
